<template>
  <div>
    <v-text-field
      ref="searchBarInput"
      v-model="query"
      prepend-inner-icon="search"
      type="search"
      label="Recherche rapide"
      color="info"
      clearable
      class="search-bar"
      autocomplete="off"
      @keyup="search($event)"
      @focus="setup()"
      @click:clear="clearQuery()"
    />

    <div v-if="isFocused" ref="overlay" class="overlay" @click="close"></div>

    <v-list v-show="query && isFocused" v-if="results" two-line class="scroll-y file-list" light>
      <v-subheader v-if="loadingSearch">Loading...</v-subheader>
      <v-subheader v-else-if="results.count">{{ results.count }} results</v-subheader>
      <v-subheader v-else>No results were found.</v-subheader>

      <v-list-item
        v-for="item in results.items"
        v-if="!loadingSearch"
        :key="item.objectID"
        @click="edit(item)"
      >
        <v-list-item-avatar class="align-content-center">
          <v-tooltip left color="grey" z-index="9">
            <template #activator="{ on }">
              <v-avatar color="info" size="40" v-on="on">
                <span class="headline text-capitalize white--text" color="white">{{ item.wtype[0] }}</span>
              </v-avatar>
              <!--<v-icon class="info white--text" v-on="on">assignment</v-icon>-->
            </template>
            <span class="text-capitalize">{{ item.wtype }}</span>
          </v-tooltip>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title v-text="item.wtitle" />
          <v-list-item-subtitle v-text="item.lastUpdateUserName" />
        </v-list-item-content>
        <v-list-item-action v-if="hasStatus(item)">
          <search-status-display
            v-if="!isFolder(item)"
            :published="item.publishedAt !== null"
            :states="getStates(item)"
            :end-publication-date="item['endPublicationDate']"
            :is-online="item['online']"
            :status="item.status"
          />
          <v-list-item-action-text v-if="!isFolder(item)">{{ new Date(item.lastUpdateTime * 1000).toLocaleString('fr-FR') }}</v-list-item-action-text>
        </v-list-item-action>
      </v-list-item>
    </v-list>
  </div>
</template>

<script setup>

import { ref, computed, nextTick } from 'vue';

import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';

import { useQuery } from '@vue/apollo-composable';

import SearchStatusDisplay from '@/components/common/SearchStatusDisplay';

import { getGraphqlSchema } from '../../graphql/index';
import { getWorkflowStates, isFolder, editContent } from '@/utils/content';

import { checkIfHasChanges } from '@/utils/pwa';

const route = useRoute();
const router = useRouter();

const store = useStore();

const query = ref(route.params.searchText);

const isFocused = ref(false);
const results = ref(null);
const searchBarInput = ref(null);

// Wildcard expression (*) used on fields to search on all fields with a specific prefix
let fieldsToSearch = store.state.searchFields;

if (store.getters.workspace.settings.navBarSearchFields) {
  fieldsToSearch = fieldsToSearch.concat(store.getters.workspace.settings.navBarSearchFields);
}

const clearQuery = () => {
  // router.push({ name: 'search' });
  query.value = '';
};

const searchVariables = computed(() => {
  const { searchRestrictionsFilters } = store.getters;

  return {
    input: {
      query: {
        bool: {
          // Add restrictions to search
          must: [
            ...[{
              simple_query_string: {
                query: `${query.value}*`,
                fields: fieldsToSearch
              }
            }],
            ...searchRestrictionsFilters.query.bool.must
          ],
          // Add restrictions to search
          must_not: searchRestrictionsFilters.query.bool.must_not,
          filter: [
            {
              terms: {
                workspace: [
                  store.getters.workspace.code
                ]
              }
            }
          ]
        }
      },
      from: 0,
      size: 5,
      sort: []
    }
  };
});

const enabled = computed(() => {
  if (query.value && isFocused.value) {
    return true;
  }
  return false;
});

const { onResult, loading: loadingSearch } = useQuery(
  getGraphqlSchema('openSearch'),
  searchVariables,
  { enabled: enabled, fetchPolicy: 'no-cache' }
);

onResult(result => {
  if (result.loading) {
    return;
  }

  results.value = result.data.search;
});

const hasStatus = item => item.hasOwnProperty('status');

const getStates = content => getWorkflowStates(content, store.state.rawContentTypes, store.state.workflows);

const edit = content => {
  isFocused.value = false;
  document.documentElement.style.overflow = 'auto';
  checkIfHasChanges(() => {
    if (content.wtype === 'folder') {
      let { path } = content;

      // Path must end with '/'
      if (content.path.slice(-1) !== '/') {
        path += '/';
      }

      const full_path = `/folders${path}${content.name}`;
      if (full_path !== route.fullPath) {
        // this.clearCacheKeepAlive()
        router.push(full_path);
      }
    } else {
      if (route.params && content.uuid === route.params.uuid) {
        // avoid navigate to same path
        return;
      }
      editContent(content, router);
    }
  });
};

function close () {
  // Closes search autocomplete
  // Allows scroll again
  isFocused.value = false;
  document.documentElement.style.overflow = 'auto';
}

function setup () {
  console.log('SETUP METHOD');
  // When autocomplete is active
  isFocused.value = true;
  document.documentElement.style.overflow = 'hidden';
}

function search (e) {
  if (e.keyCode === 13) {
    // isFocused.value = false;
    checkIfHasChanges(() => {
      store.commit('clearFilters');
      // this.clearCacheKeepAlive();
      if (query.value && route.path !== `/search/${encodeURIComponent(query.value)}`) {
        // avoid duplicate navigation error
        router.push({
          name: 'search',
          params: { searchText: query.value }
        });
      } else if (!query.value && route.path !== '/search') {
        router.push({ name: 'search' });
      }
    }, () => {
      console.log('A');
      // searchBarInput.value.blur();
      // nextTick(() => {
      //   console.log("B")
      //   searchBarInput.value.blur();
      // });
    });

    // Allows scroll
    close();
  } else {
    setup();
  }
}

</script>

<style>
    /***************
    Input bar search style
    ***************/
    .search-bar {
      margin-top: 25px !important;
      z-index: 2;
    }

    .search-bar > .v-input__control {
      z-index: 3 !important;
    }

    @media (min-width: 1320px) {
      .search-bar {
        width: 550px;
      }
    }

    @media (min-width: 601px) and (max-width: 824px) {
      .search-bar {
        width: 400px;
      }
    }

    @media (max-width: 650px) {
      .search-bar {
        width: 200px;
      }
    }

    .overlay {
      position: absolute;
      background-color: #000;
      top: 0;
      left: 0;
      z-index: 1;
      width: 100%;
      height: 100vh;
      opacity: 0.5;
    }

    /***************
    Results styling
    ***************/
    .file-list {
        position: absolute;
        max-height: calc(100vh - 75px);
        z-index: 2;
    }

     @media (min-width: 825px){
      .file-list {
        width:650px;
      }
    }

    @media (min-width: 601px) and (max-width: 824px){
      .file-list {
        width:500px;
      }
    }

    @media (max-width: 600px) {
      .file-list {
        width:350px;
      }
    }

    .v-list__tile__avatar{
       min-width: 56px !important;
    }
</style>
